import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Upload, Save, Star } from "lucide-react";
import { api } from "../lib/api";
import { QJ_BESCHIKBAARHEID, QJ_KEY_SPECS, qjStatus, QJ_FALLBACK_IMG, QJ_CATEGORIES, qjPrijs } from "../lib/qj";

const fld = "w-full bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]";
const lbl = "font-mono-tt text-[11px] uppercase tracking-wider text-[#9A9CA3] block mb-1";

const empty = {
  naam: "", categorie: "", categorieen: [], prijs: "", korte_omschrijving: "", beschrijving: "", images: [],
  cilinderinhoud: "", vermogen: "", koppel: "", gewicht: "", zithoogte: "", tankinhoud: "",
  specs: [], kleuren: [], beschikbaarheid: "leverbaar", volgorde: 0,
};

function RowList({ title, rows, onChange, fields, addLabel, testid }) {
  const upd = (i, k, v) => onChange(rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={testid}>
      <label className={lbl}>{title}</label>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2">
            {fields.map((f) => (
              <input key={f.k} type={f.type || "text"} placeholder={f.ph} value={r[f.k] || (f.type === "color" ? "#1C1D22" : "")}
                onChange={(e) => upd(i, f.k, e.target.value)} className={f.type === "color" ? "w-12 h-[42px] bg-[#0A0A0B] border border-[#2A2B31] p-1" : fld} />
            ))}
            <button type="button" onClick={() => onChange(rows.filter((_, x) => x !== i))} className="text-[#9A9CA3] hover:text-[#E10600] px-2"><X size={16} /></button>
          </div>
        ))}
      </div>
      <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...rows, Object.fromEntries(fields.map((f) => [f.k, f.type === "color" ? "#1C1D22" : ""]))])}
        className="mt-2 text-xs font-display uppercase text-[#E10600] hover:text-white flex items-center gap-1"><Plus size={14} /> {addLabel}</button>
    </div>
  );
}

function QJForm({ initial, onClose, onSaved, compressImage }) {
  const [f, setF] = useState({ ...empty, ...initial, prijs: initial?.prijs ?? "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCat = (l) => setF((p) => {
    const cur = p.categorieen || [];
    const categorieen = cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l];
    return { ...p, categorieen, categorie: categorieen[0] || "" };
  });

  const addImages = async (files) => {
    setUploading(true);
    try { const arr = []; for (const file of files) arr.push(await compressImage(file)); setF((p) => ({ ...p, images: [...p.images, ...arr] })); }
    catch { setErr("Foto verwerken mislukt. Gebruik een JPG/PNG."); }
    finally { setUploading(false); }
  };
  const makeMain = (i) => set("images", [f.images[i], ...f.images.filter((_, x) => x !== i)]);

  const save = async () => {
    setBusy(true); setErr("");
    const payload = { ...f, prijs: f.prijs !== "" && f.prijs !== null ? parseFloat(f.prijs) : null, volgorde: parseInt(f.volgorde) || 0,
      specs: f.specs.filter((s) => s.label && s.waarde), kleuren: f.kleuren.filter((k) => k.naam) };
    try {
      if (initial?.id) await api.put(`/qj-models/${initial.id}`, payload); else await api.post("/qj-models", payload);
      onSaved();
    } catch { setErr("Opslaan mislukt. Tip: gebruik minder of kleinere foto's."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="qj-form-modal">
      <div className="bg-[#141518] border border-[#2A2B31] w-full max-w-3xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2B31]">
          <h3 className="font-display uppercase font-700 text-xl text-white">{initial?.id ? "QJ-model bewerken" : "Nieuw QJ-model"}</h3>
          <button onClick={onClose} data-testid="qj-form-close" className="text-[#9A9CA3] hover:text-white"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={lbl}>Modelnaam *</label><input data-testid="qf-naam" value={f.naam} onChange={(e) => set("naam", e.target.value)} className={fld} placeholder="bv. QJ Motor SRK 700" /></div>
            <div><label className={lbl}>Verkoopprijs (€)</label><input data-testid="qf-prijs" type="number" value={f.prijs} onChange={(e) => set("prijs", e.target.value)} className={fld} placeholder="Leeg = op aanvraag" /></div>
            <div className="sm:col-span-2" data-testid="qf-categorieen">
              <label className={lbl}>Categorieën (aanvinken; eerste = hoofdcategorie)</label>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {QJ_CATEGORIES.map((c) => (
                  <label key={c.slug} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                    <input type="checkbox" data-testid={`qf-cat-${c.slug}`} className="accent-[#E10600] w-4 h-4"
                      checked={(f.categorieen || []).includes(c.label)} onChange={() => toggleCat(c.label)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={lbl}>Beschikbaarheid</label>
              <select data-testid="qf-beschikbaarheid" value={f.beschikbaarheid} onChange={(e) => set("beschikbaarheid", e.target.value)} className={fld}>
                {Object.entries(QJ_BESCHIKBAARHEID).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Volgorde (laag = eerst)</label><input data-testid="qf-volgorde" type="number" value={f.volgorde} onChange={(e) => set("volgorde", e.target.value)} className={fld} /></div>
          </div>
          <div><label className={lbl}>Korte omschrijving (productkaart)</label><input data-testid="qf-kort" value={f.korte_omschrijving} onChange={(e) => set("korte_omschrijving", e.target.value)} className={fld} /></div>
          <div><label className={lbl}>Uitgebreide beschrijving</label><textarea data-testid="qf-beschrijving" rows={4} value={f.beschrijving} onChange={(e) => set("beschrijving", e.target.value)} className={fld} /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {QJ_KEY_SPECS.map((s) => (
              <div key={s.key}><label className={lbl}>{s.label}</label><input data-testid={`qf-${s.key}`} value={f[s.key]} onChange={(e) => set(s.key, e.target.value)} className={fld} /></div>
            ))}
          </div>
          <RowList title="Extra technische specificaties" rows={f.specs} onChange={(v) => set("specs", v)} addLabel="Specificatie toevoegen" testid="qf-specs"
            fields={[{ k: "label", ph: "bv. Remmen" }, { k: "waarde", ph: "bv. ABS, dubbele schijf" }]} />
          <RowList title="Beschikbare kleuren" rows={f.kleuren} onChange={(v) => set("kleuren", v)} addLabel="Kleur toevoegen" testid="qf-kleuren"
            fields={[{ k: "naam", ph: "bv. Grijs/Rood" }, { k: "hex", type: "color" }, { k: "hex2", ph: "2e kleur (optioneel) bv. #C81428" }]} />
          <div>
            <label className={lbl}>Foto's (eerste = hoofdfoto)</label>
            <label className="inline-flex items-center gap-2 border border-dashed border-[#2A2B31] hover:border-[#E10600] px-4 py-3 cursor-pointer text-sm text-[#9A9CA3]">
              <Upload size={16} /> {uploading ? "Verwerken..." : "Foto's uploaden"}
              <input data-testid="qf-images" type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => addImages(Array.from(e.target.files))} />
            </label>
            {f.images.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3">
                {f.images.map((img, i) => (
                  <div key={i} className={`relative aspect-square border ${i === 0 ? "border-[#E10600]" : "border-[#2A2B31]"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => set("images", f.images.filter((_, x) => x !== i))} className="absolute top-0 right-0 bg-[#E10600] text-white p-1"><X size={12} /></button>
                    {i > 0 && <button type="button" title="Maak hoofdfoto" onClick={() => makeMain(i)} className="absolute bottom-0 left-0 bg-black/70 text-white p-1"><Star size={12} /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#2A2B31] flex items-center justify-end gap-3">
          {err && <p data-testid="qf-error" className="text-[#E10600] text-sm mr-auto">{err}</p>}
          <button onClick={onClose} className="border border-[#2A2B31] text-white px-5 py-2.5 font-display uppercase text-sm">Annuleer</button>
          <button data-testid="qf-save" onClick={save} disabled={busy || uploading || !f.naam}
            className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-60 text-white px-6 py-2.5 font-display uppercase font-700 text-sm flex items-center gap-2">
            <Save size={16} /> {busy ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QJBeheer({ compressImage, onCount }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/qj-models").then((r) => { setList(r.data); onCount?.(r.data.length); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (id) => { if (window.confirm("Dit QJ-model verwijderen?")) { await api.delete(`/qj-models/${id}`); load(); } };

  return (
    <div data-testid="qj-admin">
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading-xl text-2xl text-white">QJ Motor-modellen</h2>
        <button data-testid="add-qj" onClick={() => setForm(empty)}
          className="bg-[#E10600] hover:bg-[#B80500] text-white px-5 py-2.5 font-display uppercase font-700 text-sm flex items-center gap-2"><Plus size={16} /> Nieuw model</button>
      </div>
      <div className="space-y-3">
        {list.map((m) => (
          <div key={m.id} data-testid={`admin-qj-${m.id}`} className="bg-[#141518] border border-[#2A2B31] p-4 flex items-center gap-4">
            <img src={m.images?.[0] || QJ_FALLBACK_IMG} alt="" className="w-20 h-16 object-cover border border-[#2A2B31]" />
            <div className="flex-1 min-w-0">
              <p className="font-display uppercase font-700 text-white">{m.naam}</p>
              <p className="text-[#9A9CA3] text-sm font-mono-tt">{(m.categorieen?.length ? m.categorieen.join(", ") : m.categorie) || "—"} · {qjPrijs(m.prijs)} · {qjStatus(m.beschikbaarheid).label}</p>
            </div>
            <button onClick={() => setForm(m)} className="text-[#9A9CA3] hover:text-white p-2" data-testid={`edit-qj-${m.id}`}><Pencil size={18} /></button>
            <button onClick={() => del(m.id)} className="text-[#9A9CA3] hover:text-[#E10600] p-2" data-testid={`del-qj-${m.id}`}><Trash2 size={18} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-[#9A9CA3]">Nog geen QJ-modellen toegevoegd.</p>}
      </div>
      {form && <QJForm initial={form} compressImage={compressImage} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}
